import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Heart,
  Brain,
  Compass,
  Shield,
  Award,
  Globe2,
  Users,
  Zap,
  CheckCircle,
  Sparkles,
  Quote
} from 'lucide-react';
import { JourneyTimeline } from '../components/JourneyTimeline';
import { useLanguage } from '../contexts/LanguageContext';

export const NewAboutPage = () => {
  const { t } = useLanguage();

  // Credentials (cards) – texte depuis i18n, icônes depuis le composant
  const rawCredentials = t('about.credentials.items');
  const credentialsData = Array.isArray(rawCredentials) ? rawCredentials : [];

  const credentialsMeta = [
    { icon: Award },
    { icon: Brain },
    { icon: Zap },
    { icon: Globe2 }
  ];

  const credentials = credentialsData.map((item, index) => {
    const meta = credentialsMeta[index] || credentialsMeta[0];
    return {
      ...item,
      icon: meta.icon
    };
  });

  // Values – texte depuis i18n, icônes + gradients ici
  const rawValues = t('about.values.items');
  const valuesData = Array.isArray(rawValues) ? rawValues : [];

  const valuesMeta = [
    { icon: Heart, gradient: 'from-red-500 to-pink-600' },
    { icon: Brain, gradient: 'from-purple-600 to-blue-600' },
    { icon: Compass, gradient: 'from-cyan-600 to-teal-600' },
    { icon: Shield, gradient: 'from-orange-500 to-red-600' }
  ];

  const values = valuesData.map((item, index) => {
    const meta = valuesMeta[index] || valuesMeta[0];
    return {
      ...item,
      icon: meta.icon,
      gradient: meta.gradient
    };
  });

  // What makes us different
  const rawDifference = t('about.difference.blocks');
  const differenceSections = Array.isArray(rawDifference) ? rawDifference : [];

  // Method reasons (Systemisch / Skalierbar / Messbar)
  const rawReasons = t('about.method.reasons');
  const methodReasonsData = Array.isArray(rawReasons) ? rawReasons : [];

  const methodReasonsMeta = [
    { gradient: 'from-blue-600 to-cyan-600' },
    { gradient: 'from-purple-600 to-pink-600' },
    { gradient: 'from-orange-500 to-red-600' }
  ];

  const methodReasons = methodReasonsData.map((item, index) => {
    const meta = methodReasonsMeta[index] || methodReasonsMeta[0];
    return {
      ...item,
      gradient: meta.gradient
    };
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 pt-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full border border-purple-400/30 mb-8">
              <Sparkles className="w-4 h-4 text-purple-400 mr-2" />
              <span className="text-sm text-slate-300">
                {t('about.hero.badge')}
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
              {t('about.hero.titleLine1')}
              <br />
              <span className="gradient-text font-medium">
                {t('about.hero.titleHighlight')}
              </span>
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed font-light mb-8">
              {t('about.hero.subtitle')}
            </p>

            <div className="inline-block p-6 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
              <p className="text-lg text-slate-200 italic">
                &quot;{t('about.hero.quoteLine1')}
                <br />
                {t('about.hero.quoteLine2')}&quot;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
                {t('about.story.titleBeforeHighlight')}{' '}
                <span className="gradient-text font-medium">
                  {t('about.story.titleHighlight')}
                </span>
              </h2>

              <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
                <p>{t('about.story.p1')}</p>

                <p>{t('about.story.p2')}</p>

                <div className="p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-2xl border-l-4 border-purple-600">
                  <p className="text-xl font-medium text-slate-900 italic">
                    &quot;{t('about.story.quote')}&quot;
                  </p>
                </div>

                <p>{t('about.story.p3')}</p>

                <p className="text-slate-900 font-medium">
                  {t('about.story.closing')}
                </p>
              </div>
            </div>

            {/* Image / Visual Element */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt={t('about.hero.imageAlt')}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-orange-500/20" />
                </div>

                {/* Floating Quote */}
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-200 max-w-xs">
                  <Quote className="w-8 h-8 text-purple-600 mb-2" />
                  <p className="text-sm text-slate-700 italic">
                    &quot;{t('about.story.floatingQuote')}&quot;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('about.credentials.title')}{' '}
              <span className="gradient-text font-medium">
                {t('about.credentials.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t('about.credentials.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {credentials.map((cred, index) => (
              <div
                key={cred.id || index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <cred.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  {cred.title}
                </h3>
                <p className="text-sm font-medium text-purple-600 mb-2">
                  {cred.subtitle}
                </p>
                <p className="text-sm text-slate-600">{cred.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('about.journey.title')}{' '}
              <span className="gradient-text font-medium">
                {t('about.journey.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t('about.journey.subtitle')}
            </p>
          </div>

          <JourneyTimeline />
        </div>
      </section>

      {/* Philosophy & Values */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              {t('about.values.title')}{' '}
              <span className="gradient-text font-medium">
                {t('about.values.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              {t('about.values.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={value.id || index} className="group">
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300">
                  <div
                    className={`w-14 h-14 bg-gradient-to-br ${value.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('about.difference.title')}{' '}
              <span className="gradient-text font-medium">
                {t('about.difference.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t('about.difference.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {differenceSections.map((section, index) => (
              <div key={section.id || index}>
                <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-200">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">
                    {index === 0 ? '❌ ' : '✅ '}
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {Array.isArray(section.points) &&
                      section.points.map((point, idx) => (
                        <li
                          key={idx}
                          className="flex items-start text-slate-700"
                        >
                          {index === 0 ? (
                            <span className="text-red-500 mr-2">•</span>
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          )}
                          <span>{point}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-purple-50 to-orange-50 rounded-2xl border-2 border-purple-200">
            <p className="text-xl text-slate-800 leading-relaxed text-center">
              <strong>{t('about.difference.promiseLead')}</strong>{' '}
              {t('about.difference.promiseText')}
            </p>
          </div>
        </div>
      </section>

      {/* TRIAX® Integration */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('about.method.title')}{' '}
              <span className="gradient-text font-medium">
                {t('about.method.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t('about.method.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 border-2 border-purple-200 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {t('about.method.triax.title')}
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('about.method.triax.text')}
              </p>
              <Link
                to="/triax"
                className="inline-flex items-center text-purple-600 font-medium hover:text-purple-700"
              >
                {t('about.method.triax.linkLabel')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-orange-200 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {t('about.method.rff.title')}
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('about.method.rff.text')}
              </p>
              <Link
                to="/services"
                className="inline-flex items-center text-orange-600 font-medium hover:text-orange-700"
              >
                {t('about.method.rff.linkLabel')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>

          <div className="p-8 bg-white rounded-2xl border-2 border-slate-200">
            <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
              {t('about.method.reasonsTitle')}
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              {methodReasons.map((reason, index) => (
                <div key={reason.id || index} className="text-center">
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${reason.gradient} rounded-full flex items-center justify-center mx-auto mb-3`}
                  >
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm text-slate-700">
                    <strong>{reason.title}:</strong> {reason.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light mb-6">
            {t('about.cta.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('about.cta.highlight')}
            </span>
            ?
          </h2>
          <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            {t('about.cta.text')}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-lg transition-all duration-300 hover:shadow-glow hover:scale-105"
          >
            <span className="mr-3">{t('about.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
